import { ArrowSmRightIcon } from '@heroicons/react/solid';
import { graphql, useStaticQuery } from 'gatsby';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Autoplay, EffectCoverflow, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import AvenueGatsbyImage from '../common/AvenueGatsbyImage';
import { Container } from '../common/base';

function TestimonialSlider() {
    const { testimonial } = useStaticQuery(graphql`
        {
            testimonial: allContentfulTestimonialSlider(filter: { name: { eq: "Home Page Testimonial" } }) {
                edges {
                    node {
                        testimonial {
                            author
                            logo {
                                title
                                publicUrl
                                localFile {
                                    childImageSharp {
                                        fluid {
                                            src
                                        }
                                        gatsbyImageData(width: 50)
                                    }
                                    publicURL
                                    extension
                                }
                            }
                            quote {
                                quote
                            }
                            company
                            id
                            updatedAt
                        }
                    }
                }
            }
        }
    `);
    const unsortedSlides = testimonial.edges[0].node.testimonial;
    const slides = [...unsortedSlides].sort((a, b) =>
      new Date(b.updatedAt) - new Date(a.updatedAt)
    );

    return (
        <section className='pb-16'>
            <Container>
                <div
                    className='w-full py-5 text-center text-5xl leading-[62.1px] tracking-tighter text-avenue-slate-500 max-md:max-w-full max-md:text-4xl'
                    aria-label='Header'
                >
                    Australian businesses are moving faster with Avenue{' '}
                    <div className='relative -top-1 inline-block xl:-top-2'>
                        <ArrowSmRightIcon className='h-5 lg:h-6 xl:h-8' />
                    </div>
                    <div />
                </div>
                <div className='relative max-w-[1500px]'>
                    <Swiper
                        autoHeight={false}
                        effect='coverflow'
                        rewind
                        grabCursor
                        centeredSlides
                        spaceBetween={150}
                        loop
                        speed={800}
                        slidesPerView={2}
                        lazyPreloadPrevNext={3}
                        coverflowEffect={{
                            rotate: 5,
                            stretch: 0,
                            depth: 200,
                            modifier: 1, // 1,2 or 3
                            slideShadows: false,
                        }}
                        breakpoints={{
                            200: {
                                // accommodates for all mobile device sizes
                                slidesPerView: 1,
                                spaceBetween: 100,
                            },
                            640: {
                                slidesPerView: 1,
                                spaceBetween: 100,
                            },
                            1024: {
                                slidesPerView: 2,
                                spaceBetween: 100,
                            },
                        }}
                        pagination={{
                            el: '.swiper-pagination',
                            clickable: true,
                            type: 'bullets',
                        }}
                        navigation={{
                            nextEl: '.swiper-button-next',
                            prevEl: '.swiper-button-prev',
                        }}
                        style={{ height: '100%' }}
                        modules={[EffectCoverflow, Pagination, Navigation, Autoplay]}
                        className='swiper-container relative w-10/12 overflow-visible'
                    >
                        <div className='swiper-wrapper h-[600px]'>
                            {slides.map((t) => (
                                <SwiperSlide className='mt-4 flex snap-x snap-mandatory justify-center gap-4 rounded-lg pb-10 align-middle'>
                                    <div className='h-100 w-full rounded-lg bg-avenue-parchment-400'>
                                        <div className='p-8 text-center text-avenue-slate-500'>
                                            <p className='text-lg font-medium leading-relaxed tracking-tight max-md:max-w-full sm:text-base md:text-2xl'>
                                                &quot;{t.quote.quote}&quot;
                                            </p>
                                        </div>
                                        <div className='flex items-center justify-center p-6'>
                                            <AvenueGatsbyImage
                                                className='rounded-lg object-cover'
                                                localFile={t.logo.localFile}
                                                title={t.logo.title}
                                            />
                                        </div>
                                        <div className='p-2 text-center text-avenue-slate-500'>
                                            <h2 className='m-auto mb-2 flex w-64 justify-center gap-5 px-px text-lg font-bold leading-relaxed tracking-normal text-avenue-charade-600 max-md:max-w-full max-md:flex-wrap sm:text-base'>
                                                {t.company}
                                            </h2>
                                            <h1 className='mt-2 flex justify-center gap-4 px-px pb-10 text-base leading-relaxed tracking-normal sm:text-base'>
                                                {t.author}
                                            </h1>
                                        </div>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </div>
                    </Swiper>
                    <div className='swiper-button-next !size-11 rounded-full bg-avenue-grey-200 after:font-bold after:text-white after:[--swiper-navigation-size:20px]' />
                    <div className='swiper-button-prev !size-11 rounded-full bg-avenue-grey-200 after:font-bold after:text-white after:[--swiper-navigation-size:20px]' />
                    <div className='swiper-pagination -mb-12' />
                </div>
            </Container>
        </section>
    );
}

export default TestimonialSlider;
